import { useMasters } from '../context/MastersContext';
import { 
  Type, 
  Unit, 
  CostCenter, 
  Category
} from '../types/dataHierarchy';

/**
 * Compatibility layer for data hierarchy
 * Handles the mismatch between current DB structure (unit.type) and new interface (unit.typeId)
 */
export function useDataHierarchyCompat() {
  const { types, units, costCenters, categories } = useMasters();

  // Convert current data structure to match new interfaces
  const compatibleUnits: Unit[] = units.map(unit => ({
    id: unit.id,
    name: unit.name,
    typeId: unit.type || unit.typeId, // Handle both old (type) and new (typeId) fields
    description: unit.description,
    isActive: unit.isActive !== false, // Default to true if not specified
    createdAt: unit.createdAt,
    updatedAt: unit.updatedAt
  }));

  const compatibleCostCenters: CostCenter[] = costCenters.map(cc => ({
    id: cc.id,
    name: cc.name,
    typeId: cc.type || cc.typeId, // Handle both old (type) and new (typeId) fields
    description: cc.description,
    displayName: cc.displayName,
    isActive: cc.isActive !== false,
    createdAt: cc.createdAt,
    updatedAt: cc.updatedAt
  }));

  const compatibleCategories: Category[] = categories.map(cat => ({
    id: cat.id,
    name: cat.name,
    typeId: cat.type || cat.typeId, // Handle both old (type) and new (typeId) fields
    costCenterId: cat.costCenter || cat.costCenterId, // Handle both formats
    description: cat.description,
    needsNameField: cat.requiresNameField || cat.needsNameField, // Handle both database field and legacy field
    supportsUnits: cat.supportsUnits,
    unitLabel: cat.unitLabel,
    isActive: cat.isActive !== false,
    createdAt: cat.createdAt,
    updatedAt: cat.updatedAt
  }));

  const compatibleTypes: Type[] = types.map(type => ({
    id: type.id,
    name: type.name,
    description: type.description,
    createdAt: type.createdAt,
    updatedAt: type.updatedAt
  }));

  // MASTER DATA FILTERING (Definition Level)
  const getMasterDataFilters = (selectedTypeId: string) => {
    return {
      typeId: selectedTypeId,
      availableUnits: compatibleUnits.filter(unit => unit.typeId === selectedTypeId),
      availableCostCenters: compatibleCostCenters.filter(cc => cc.typeId === selectedTypeId),
      availableCategories: compatibleCategories.filter(cat => cat.typeId === selectedTypeId)
    };
  };

  // TYPE-BASED FILTERING FOR FORM COMPONENTS
  const getFilteredOptions = (selectedTypeId: string) => {
    if (!selectedTypeId) {
      return {
        units: [],
        costCenters: [],
        categories: []
      };
    }

    const typeUnits = compatibleUnits.filter(unit => unit.typeId === selectedTypeId);
    const typeCostCenters = compatibleCostCenters.filter(cc => cc.typeId === selectedTypeId);
    const typeCategories = compatibleCategories.filter(cat => cat.typeId === selectedTypeId);

    return {
      units: typeUnits.map(unit => ({
        value: unit.id,
        label: unit.name,
        typeId: unit.typeId
      })),
      costCenters: typeCostCenters.map(cc => ({
        value: cc.id,
        label: cc.displayName || cc.name,
        typeId: cc.typeId,
        originalName: cc.name
      })),
      categories: typeCategories.map(cat => ({
        value: cat.id,
        label: cat.name,
        typeId: cat.typeId,
        costCenterId: cat.costCenterId
      }))
    };
  };

  // CATEGORY FILTERING BY COST CENTER
  const getCategoriesForCostCenter = (costCenterId: string, typeId?: string) => {
    let filteredCategories = compatibleCategories.filter(cat => 
      cat.costCenterId === costCenterId
    );

    if (typeId) {
      filteredCategories = filteredCategories.filter(cat => cat.typeId === typeId);
    }

    return filteredCategories.map(cat => ({
      value: cat.id,
      label: cat.name,
      costCenterId: cat.costCenterId,
      typeId: cat.typeId
    }));
  };

  // VALIDATION FUNCTIONS
  const validateComparison = (unitIds: string[], costCenterIds: string[]) => {
    const warnings: string[] = [];
    const errors: string[] = [];

    // Check if units are from the same type
    const selectedUnits = compatibleUnits.filter(unit => unitIds.includes(unit.id));
    const unitTypes = [...new Set(selectedUnits.map(u => u.typeId))];

    if (unitTypes.length > 1) {
      errors.push('Cannot compare units from different business types');
    }

    // Check if cost centers are from the same type
    const selectedCostCenters = compatibleCostCenters.filter(cc => 
      costCenterIds.includes(cc.id)
    );
    const ccTypes = [...new Set(selectedCostCenters.map(cc => cc.typeId))];

    if (ccTypes.length > 1) {
      errors.push('Cannot compare cost centers from different business types');
    }

    // Check if units and cost centers are from the same type
    if (unitTypes.length === 1 && ccTypes.length === 1 && unitTypes[0] !== ccTypes[0]) {
      errors.push('Units and cost centers must be from the same business type');
    }

    return {
      isValid: errors.length === 0,
      warnings,
      errors
    };
  };

  // GET TYPE INFORMATION
  const getTypeInfo = (typeId: string) => {
    const type = compatibleTypes.find(t => t.id === typeId);
    if (!type) return null;

    const typeUnits = compatibleUnits.filter(unit => unit.typeId === typeId);
    const typeCostCenters = compatibleCostCenters.filter(cc => cc.typeId === typeId);
    const typeCategories = compatibleCategories.filter(cat => cat.typeId === typeId);

    return {
      type,
      unitCount: typeUnits.length,
      costCenterCount: typeCostCenters.length,
      categoryCount: typeCategories.length
    };
  };

  return {
    // Compatible data
    units: compatibleUnits,
    costCenters: compatibleCostCenters,
    categories: compatibleCategories,
    types: compatibleTypes,

    // Core filtering functions
    getMasterDataFilters,
    getFilteredOptions,
    getCategoriesForCostCenter,
    
    // Validation functions
    validateComparison,
    
    // Information functions
    getTypeInfo,
  };
}
